import { Spin, Typography, Row, Col, Flex, Checkbox } from 'antd'
import { useState, useEffect } from 'react';
import { emailRules, phoneNumberRules } from '../../../helpers/helpers'
import { useSelector, useDispatch } from "react-redux";
import { kycHistory, getAadhaarAndPan, getConsentDigilocker, busKycHistory, busGetAadhaarAndPan, busGetConsentDigilocker } from '../../../redux/slices/EnqMngtKycSlice';
import { TITLE_TXT, BTN_TXT } from '../../../constants/constants';
import CustomInput from '../../../components/CustomInput/Input';
import { useSearchParams } from 'react-router-dom';
import PrimaryButton from '../../../components/CustomButton/PrimaryButton';

const { Text } = Typography;

const DIGI_CONSENT_STORAGE_PREFIX = "digilockerConsent:";

const getDigiConsentStorageKey = (orgKey) =>
    orgKey ? `${DIGI_CONSENT_STORAGE_PREFIX}${orgKey}` : null;

const readStoredDigiConsent = (orgKey) => {
    try {
        const key = getDigiConsentStorageKey(orgKey);
        return Boolean(key && sessionStorage.getItem(key) === "1");
    } catch {
        return false;
    }
};

const persistDigiConsent = (orgKey) => {
    try {
        const key = getDigiConsentStorageKey(orgKey);
        if (key) sessionStorage.setItem(key, "1");
    } catch {
        // ignore storage failures (private mode, etc.)
    }
};

const clearStoredDigiConsent = (orgKey) => {
    try {
        const key = getDigiConsentStorageKey(orgKey);
        if (key) sessionStorage.removeItem(key);
    } catch {
        // ignore
    }
};

const KycContactDirector = ({ kycData, email, phoneNo, orgId, form, isReview, organizationId, token, dealCategoryId }) => {
    const dispatch = useDispatch()
    const [searchParams] = useSearchParams();
    const params = searchParams.get("status");
    const { loading, kycHistoryData, digilockerLoading, pendingKycData } =
        useSelector((state) => state.enqMngt);
    const aadhaarDetails =
        kycHistoryData?.kycDetails?.kycDirectorsAadhaarValue ||
        kycData?.kycDirectorsAadhaarValue;
    const panDetails =
        kycHistoryData?.kycDetails?.kycDirectorsPanValue ||
        kycData?.kycDirectorsPanValue;
    // DigiLocker and manual Approve both settle when Aadhaar + PAN are verified
    const isDirectorKycVerified = Boolean(
        aadhaarDetails?.isVerified && panDetails?.isVerified,
    );
    const orgKey = orgId || organizationId;
    const isDigiLockerReturnSuccess = params === "digilocker-consent-success";
    const [hasStoredDigiConsent, setHasStoredDigiConsent] = useState(() =>
        readStoredDigiConsent(orgKey),
    );
    // Consent stays locked after DigiLocker return even if the URL query is cleared
    const isDigiConsentCompleted = isDigiLockerReturnSuccess || hasStoredDigiConsent;
    const isDigiConsentLocked = isDigiConsentCompleted || isDirectorKycVerified;
    const canFetchAadhaarPan = isDigiConsentCompleted;
    const [isDigiConsentGiven, setIsDigiConsentGiven] = useState(isDigiConsentCompleted);

    useEffect(() => {
        if (isDigiLockerReturnSuccess) {
            persistDigiConsent(orgKey);
            setHasStoredDigiConsent(true);
            setIsDigiConsentGiven(true);
        }
    }, [isDigiLockerReturnSuccess, orgKey]);

    useEffect(() => {
        if (isDirectorKycVerified) {
            clearStoredDigiConsent(orgKey);
        }
    }, [isDirectorKycVerified, orgKey]);

    useEffect(() => {
        const details = kycHistoryData?.kycDetails || kycData;
        const patch = {};

        if (details?.kycDirectorsAadhaarValue?.isVerified) {
            patch.dirAadhaar = details.kycDirectorsAadhaarValue?.docValue;
            patch.dirName =
                kycHistoryData?.directorName ||
                pendingKycData?.directorName ||
                form.getFieldValue("dirName");
        }

        if (details?.kycDirectorsPanValue?.isVerified) {
            patch.dirPan = details.kycDirectorsPanValue?.docValue;
        }

        if (Object.keys(patch).length) {
            form.setFieldsValue(patch);
        }
    }, [form, isDirectorKycVerified, kycHistoryData, kycData, pendingKycData]);

    const fetchAadhaarPan = async () => {
        let response;
        if (token) {
            response = await dispatch(busGetAadhaarAndPan({ orgId: organizationId, token, dealCategoryId }));
        } else {
            response = await dispatch(getAadhaarAndPan({ orgId, dealCategoryId }));
        }

        if (response?.payload?.statusCode === 201) {
            let res;
            if (token) {
                res = await dispatch(busKycHistory({ orgId: organizationId, token }));
            } else {
                res = await dispatch(kycHistory(orgId));
            }
            if (res?.payload?.directorName || res?.payload?.kycDetails) {
                form.setFieldsValue({
                    dirName: res?.payload?.directorName,
                    dirAadhaar: res?.payload?.kycDetails?.kycDirectorsAadhaarValue?.docValue,
                    dirPan: res?.payload?.kycDetails?.kycDirectorsPanValue?.docValue,
                });
            }
        }
    };

    const handleProceedDigi = async () => {
        let res;
        if (token) {
            res = await dispatch(busGetConsentDigilocker({ orgId: organizationId, token, dealCategoryId }));
        } else {
            res = await dispatch(getConsentDigilocker({ orgId, dealCategoryId }));
        }

        if (res?.payload.response) {
            window.location.href = res.payload.response;
        }
    }

    return (
        <Spin spinning={loading}>
            <Text className='kyc-subtitle'>{TITLE_TXT.CONTACT_DETS}</Text>
            <Row gutter={[24, 0]}>
                <Col xs={24} md={12}>
                    <CustomInput
                        label="Email Address"
                        name="email"
                        type="email"
                        isRequired={true}
                        placeholder="Enter Contact Email"
                        rules={emailRules("Contact")}
                        disabled
                    />
                </Col>
                <Col xs={24} md={12}>
                    <CustomInput
                        label="Mobile Number"
                        name="phoneNo"
                        isRequired={true}
                        placeholder="Enter Contact Phone Number"
                        rules={phoneNumberRules("Contact")}
                        disabled
                        prefix="+91"
                    />
                </Col>
            </Row>
            <Text className='kyc-subtitle'>{TITLE_TXT.DIR_KYC}</Text>
            {isDirectorKycVerified ? (
                <Row gutter={[24, 0]}>
                    <Col xs={24} md={12}>
                        <CustomInput
                            label="Director Name"
                            name="dirName"
                            type="text"
                            isRequired={true}
                            disabled={isReview || isDirectorKycVerified}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <CustomInput
                            label="Director Aadhaar"
                            name="dirAadhaar"
                            type="text"
                            isRequired={true}
                            disabled={isReview || isDirectorKycVerified}
                        />
                    </Col>
                    <Col xs={24} md={12}>
                        <CustomInput
                            label="Director PAN"
                            name="dirPan"
                            type="text"
                            isRequired={true}
                            disabled={isReview || isDirectorKycVerified}
                        />
                    </Col>
                </Row>
            ) : isReview ? (
                <Text type="secondary">Director KYC details are locked.</Text>
            ) : (
                <Flex align='center' gap={20} wrap>
                    <Checkbox
                        style={{ margin: '16px 0' }}
                        checked={isDigiConsentGiven}
                        disabled={isDigiConsentLocked}
                        onChange={(e) => {
                            if (!isDigiConsentLocked) {
                                setIsDigiConsentGiven(e.target.checked);
                            }
                        }}
                    >
                        I hereby give consent to fetch my KYC details via DigiLocker
                    </Checkbox>
                    <PrimaryButton
                        disabled={!isDigiConsentGiven || isDigiConsentLocked}
                        loading={digilockerLoading}
                        onClick={handleProceedDigi}
                    >
                        {BTN_TXT.PRO_WITH_DIGI}
                    </PrimaryButton>
                    <PrimaryButton disabled={!canFetchAadhaarPan} loading={digilockerLoading} onClick={fetchAadhaarPan}>
                        {BTN_TXT.FETCH_AADHAAR_PAN}
                    </PrimaryButton>
                </Flex >
            )}
        </Spin>
    )
}

export default KycContactDirector
